import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toPng } from "html-to-image";

const SITE_NAME = "منصة هِمّة";
const SITE_LOGO = "/logo192.png";
const EXPORT_FILENAME = "ramadan-schedule.png";

const RAMADAN_API = "/.netlify/functions/api/ramadan/schedule";

const DAY_OPTIONS = [
  { key: "U", label: "الأحد" },
  { key: "M", label: "الإثنين" },
  { key: "T", label: "الثلاثاء" },
  { key: "W", label: "الأربعاء" },
  { key: "R", label: "الخميس" },
];

const TIME_MAP = new Map([
  ["08:00", "10:00"], ["09:00", "10:40"], ["09:30", "11:00"],
  ["10:00", "11:20"], ["10:30", "11:40"], ["11:00", "13:00"],
  ["12:00", "13:40"], ["13:00", "14:20"], ["14:00", "15:00"],
  ["14:30", "15:20"], ["15:00", "15:40"], ["16:00", "16:20"],
  ["17:00", "21:20"], ["17:30", "21:40"], ["18:00", "22:00"],
  ["18:30", "22:20"], ["19:00", "22:40"], ["19:30", "23:00"],
  ["20:00", "23:20"], ["21:00", "00:00"], ["22:00", "00:40"],
  ["23:00", "01:20"],
]);

const DURATION_MAP = { "50": "35", "75": "55", "100": "70" };

const toMinutes = (hhmm) => {
  const [h, m] = String(hhmm || "").split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return NaN;
  return h * 60 + m;
};

const pad2 = (n) => String(n).padStart(2, "0");

const isValidHHMM = (s) => {
  if (typeof s !== "string") return false;
  const m = s.match(/^(\d{2}):(\d{2})$/);
  if (!m) return false;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  return hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59;
};

const normalizeTime = (s) => {
  if (!isValidHHMM(s)) return "";
  const [hh, mm] = s.split(":").map(Number);
  return `${pad2(hh)}:${pad2(mm)}`;
};

const formatTime12 = (hhmm) => {
  if (!isValidHHMM(hhmm)) return "—";
  const [hStr, mStr] = hhmm.split(":");
  const h = Number(hStr);
  const m = Number(mStr);
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad2(m)} ${suffix}`;
};

const nearestTimes = (input) => {
  const inputMin = toMinutes(input);
  const keys = Array.from(TIME_MAP.keys())
    .map((k) => ({ k, m: toMinutes(k) }))
    .sort((a, b) => a.m - b.m);

  let i = keys.findIndex((x) => x.m >= inputMin);
  if (i === -1) i = keys.length;

  const prev = keys[(i - 1 + keys.length) % keys.length];
  const next = keys[i % keys.length];

  const diffCirc = (a, b) => {
    const d = Math.abs(a - b);
    return Math.min(d, 24 * 60 - d);
  };

  return { prev: prev.k, next: next.k, dPrev: diffCirc(inputMin, prev.m), dNext: diffCirc(inputMin, next.m) };
};

const isRemoteLikely = (ramadanTime) => {
  const m = toMinutes(ramadanTime);
  if (!Number.isFinite(m)) return false;
  return m >= toMinutes("21:00") || m < toMinutes("03:00");
};

const ratingColor = (pct) => {
  if (pct >= 80) return "rgba(162, 233, 189, 1)";
  if (pct >= 60) return "rgba(242, 194, 102, 1)";
  if (pct >= 30) return "rgba(250, 134, 134, 1)";
  return "rgba(255, 0, 0, 1)";
};

function ProgressCircle({ pct }) {
  const color = ratingColor(pct);
  const radius = 35;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;
  return (
    <div className="relative w-[80px] h-[80px] md:w-[120px] md:h-[120px]">
      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={radius} stroke="#374151" strokeWidth="6" fill="transparent" />
        <circle
          cx="60"
          cy="60"
          r={radius}
          stroke={color}
          strokeWidth="6"
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-700 ease-out"
        />
        <circle cx="60" cy="25" r="3" fill={color} className="transition-all duration-700 ease-out" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-white text-sm md:text-2xl font-bold">{pct}%</span>
      </div>
    </div>
  );
}

const parseJsonSafe = async (r) => {
  const txt = await r.text();
  try {
    return { ok: r.ok, status: r.status, json: JSON.parse(txt || "{}"), text: txt };
  } catch {
    return { ok: r.ok, status: r.status, json: null, text: txt };
  }
};

const throwApiError = (out) => {
  const msg = out?.json?.message || out?.json?.error || out?.text || `HTTP ${out?.status ?? "?"}`;
  const err = new Error(msg);
  err.status = out?.status;
  throw err;
};

const apiLoadSchedule = async () => {
  const r = await fetch(RAMADAN_API, { method: "GET", credentials: "include" });
  const out = await parseJsonSafe(r);
  if (!out.ok) throwApiError(out);
  return out.json || {};
};

const apiSaveSchedule = async (schedule) => {
  const r = await fetch(RAMADAN_API, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ schedule }),
  });
  const out = await parseJsonSafe(r);
  if (!out.ok) throwApiError(out);
  return out.json || {};
};

const safeUuid = () => {
  try { return crypto.randomUUID(); }
  catch { return `id_${Date.now()}_${Math.random().toString(16).slice(2)}`; }
};

const normalizeDays = (days) => {
  const set = new Set();
  const arr = Array.isArray(days) ? days : [];
  for (const d of arr) {
    const k = String(d || "").trim().toUpperCase();
    if (DAY_OPTIONS.some((x) => x.key === k)) set.add(k);
  }
  return Array.from(set);
};

const sanitizeScheduleRows = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter(Boolean)
    .map((r) => {
      const id = typeof r.id === "string" && r.id.trim() ? r.id : safeUuid();
      const name = String(r.name || "").trim() || "بدون اسم";
      const before = normalizeTime(String(r.before || ""));
      const durBefore = ["50", "75", "100"].includes(String(r.durBefore)) ? String(r.durBefore) : "50";
      const days = normalizeDays(r.days);
      return { id, name, before, durBefore, days };
    })
    .filter((r) => r.before);
};

function ConfirmModal({
  open,
  title = "تأكيد",
  description = "",
  confirmText = "تأكيد",
  cancelText = "إلغاء",
  danger = false,
  busy = false,
  onConfirm,
  onClose,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      <div className="absolute inset-0 bg-black/55" onClick={busy ? undefined : onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#081423] shadow-2xl">
          <div className="p-5 border-b border-white/10">
            <div className="text-white font-black text-lg">{title}</div>
            {description ? <div className="mt-1 text-white/60 text-sm leading-6">{description}</div> : null}
          </div>
          <div className="p-5 flex items-center justify-end gap-2">
            <button
              disabled={busy}
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 disabled:opacity-50"
            >
              {cancelText}
            </button>
            <button
              disabled={busy}
              onClick={onConfirm}
              className={`px-4 py-2 rounded-xl border font-black disabled:opacity-50 ${
                danger
                  ? "border-red-500/40 bg-red-500/20 hover:bg-red-500/30 text-red-100"
                  : "border-[#28e3da55] bg-[#28e3da33] hover:bg-[#28e3da44] text-white"
              }`}
            >
              {busy ? "..." : confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DayMultiSelect({ value, onChange, disabled }) {
  const selected = new Set(normalizeDays(value));

  const toggle = (k) => {
    const next = new Set(selected);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    onChange(Array.from(next));
  };

  return (
    <div className="mt-1 flex flex-wrap gap-2">
      {DAY_OPTIONS.map((d) => {
        const active = selected.has(d.key);
        return (
          <button
            key={d.key}
            type="button"
            disabled={disabled}
            onClick={() => toggle(d.key)}
            className={`px-3 py-2 rounded-xl border text-xs font-black transition disabled:opacity-50 ${
              active
                ? "border-[#28e3da88] bg-[#28e3da33] text-white"
                : "border-white/15 bg-white/5 text-white/70 hover:bg-white/10"
            }`}
          >
            {d.label}
          </button>
        );
      })}
    </div>
  );
}

const daysLabelFromKeys = (keys) => {
  const arr = normalizeDays(keys);
  if (!arr.length) return "—";
  const map = new Map(DAY_OPTIONS.map((d) => [d.key, d.label]));
  return arr.map((k) => map.get(k) || k).join("، ");
};

const daysAbbrevFromKeys = (keys) => {
  const arr = normalizeDays(keys);
  if (!arr.length) return "—";
  return arr.join("");
};

export default function RamadanSchedulePage() {
  const [courseName, setCourseName] = useState("");
  const [beforeTime, setBeforeTime] = useState("");
  const [durBefore, setDurBefore] = useState("50");
  const [selectedDays, setSelectedDays] = useState([]);
  const [rows, setRows] = useState([]);

  const [hydrating, setHydrating] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);

  const [status, setStatus] = useState({ tone: "info", msg: "" });

  const exportRootRef = useRef(null);
  const exportScrollRef = useRef(null);

  const [confirm, setConfirm] = useState({
    open: false,
    kind: "clear",
    rowId: null,
    title: "",
    description: "",
  });

  const setOk = useCallback((msg) => setStatus({ tone: "ok", msg }), []);
  const setErr = useCallback((msg) => setStatus({ tone: "err", msg }), []);
  const setInfo = useCallback((msg) => setStatus({ tone: "info", msg }), []);

  const computed = useMemo(() => {
    const cleaned = sanitizeScheduleRows(rows);
    const out = cleaned.map((r) => {
      const ramadan = TIME_MAP.get(r.before) || "";
      const ok = Boolean(ramadan);
      const durAfter = DURATION_MAP[r.durBefore] || "";
      const remote = ramadan ? isRemoteLikely(ramadan) : false;
      const near = ok ? null : nearestTimes(r.before);
      return { ...r, ramadan, ok, durAfter, remote, near };
    });
    const okCount = out.filter((x) => x.ok).length;
    const pct = out.length ? Math.round((okCount / out.length) * 100) : 0;
    return { list: out, okCount, pct };
  }, [rows]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setInfo("جاري جلب بياناتك...");
        const data = await apiLoadSchedule();
        if (!alive) return;
        setRows(Array.isArray(data?.schedule) ? sanitizeScheduleRows(data.schedule) : []);
        setOk("تم تحميل الجدول بنجاح.");
      } catch (e) {
        if (!alive) return;
        if (e.status === 401) setErr("يجب تسجيل الدخول للوصول لجدولك.");
        else setErr("فشل تحميل البيانات من السيرفر.");
      } finally {
        if (alive) setHydrating(false);
      }
    })();
    return () => { alive = false; };
  }, [setErr, setInfo, setOk]);

  const disableActions = hydrating || exporting || savingSchedule;

  const persistRows = async (nextRows, { silent = false } = {}) => {
    const cleaned = sanitizeScheduleRows(nextRows);
    try {
      setSavingSchedule(true);
      if (!silent) setInfo("جاري حفظ التعديلات...");
      await apiSaveSchedule(cleaned);
      setRows(cleaned);
      if (!silent) setOk("تم الحفظ بنجاح.");
      return true;
    } catch (e) {
      if (e.status === 401) setErr("انتهت الجلسة، يرجى تسجيل الدخول.");
      else setErr("فشل الحفظ. حاول مرة أخرى.");
      return false;
    } finally {
      setSavingSchedule(false);
    }
  };

  const addRow = async () => {
    const t = normalizeTime(beforeTime);
    if (!t) return setErr("يرجى اختيار وقت صحيح.");

    const days = normalizeDays(selectedDays);
    if (!days.length) return setErr("يرجى اختيار يوم واحد على الأقل.");

    const next = [
      ...sanitizeScheduleRows(rows),
      { id: safeUuid(), name: courseName.trim() || "بدون اسم", before: t, durBefore, days },
    ];

    setCourseName("");
    setBeforeTime("");
    setDurBefore("50");
    setSelectedDays([]);
    setStatus({ tone: "info", msg: "" });

    await persistRows(next, { silent: true });
  };

  const askDeleteRow = (id) => {
    setConfirm({
      open: true,
      kind: "delete",
      rowId: id,
      title: "حذف مادة",
      description: "هل أنت متأكد؟ سيتم حذف المادة من جدولك وحفظ التغيير مباشرة.",
    });
  };

  const askClearAll = () => {
    setConfirm({
      open: true,
      kind: "clear",
      rowId: null,
      title: "مسح الجدول بالكامل",
      description: "هذا الإجراء لا يمكن التراجع عنه. سيتم حذف جميع المواد وحفظ التغيير مباشرة.",
    });
  };

  const onConfirmAction = async () => {
    if (confirm.kind === "delete" && confirm.rowId) {
      const next = rows.filter((x) => x.id !== confirm.rowId);
      const ok = await persistRows(next);
      if (ok) setConfirm((c) => ({ ...c, open: false }));
      return;
    }
    if (confirm.kind === "clear") {
      const ok = await persistRows([]);
      if (ok) setConfirm((c) => ({ ...c, open: false }));
    }
  };

  const saveScheduleNow = async () => {
    await persistRows(rows);
  };

const waitForImages = async (root) => {
  const imgs = Array.from(root.querySelectorAll("img"));
  await Promise.all(
    imgs.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalWidth > 0) return resolve();
          const done = () => resolve();
          img.addEventListener("load", done, { once: true });
          img.addEventListener("error", done, { once: true });
        })
    )
  );
};

const saveTableAsImage = async () => {
  const root = exportRootRef.current;
  if (!root) return;

  try {
    setExporting(true);
    setInfo("جاري تجهيز الصورة...");

    const clone = root.cloneNode(true);

    const host = document.createElement("div");
    host.style.position = "fixed";
    host.style.inset = "0";
    host.style.zIndex = "999999";
    host.style.pointerEvents = "none";
    host.style.opacity = "0";
    host.style.background = "transparent";

    clone.style.maxWidth = "none";
    clone.style.width = "auto";
    clone.style.overflow = "visible";
    clone.style.direction = "rtl";
    clone.style.color = "#ffffff";
    clone.style.background = "#081423";

    host.appendChild(clone);
    document.body.appendChild(host);

    const cloneScroll = clone.querySelector("[data-export-scroll='1']");
    if (cloneScroll) {
      cloneScroll.style.overflow = "visible";
      cloneScroll.style.maxWidth = "none";
      cloneScroll.style.width = "auto";
    }

    await new Promise((r) => requestAnimationFrame(r));
    if (document.fonts?.ready) await document.fonts.ready;
    await waitForImages(clone);
    await new Promise((r) => requestAnimationFrame(r));

    const target = cloneScroll || clone;
    const fullW = Math.ceil(Math.max(target.scrollWidth, clone.scrollWidth, clone.getBoundingClientRect().width));
    const fullH = Math.ceil(Math.max(target.scrollHeight, clone.scrollHeight, clone.getBoundingClientRect().height));

    clone.style.width = `${fullW}px`;
    if (cloneScroll) cloneScroll.style.width = `${fullW}px`;

    await new Promise((r) => requestAnimationFrame(r));

    const px = fullW >= 1400 ? 1.5 : 2;

    const dataUrl = await toPng(clone, {
      cacheBust: true,
      pixelRatio: px,
      backgroundColor: "#081423",
      width: fullW,
      height: fullH,
      style: {
        width: `${fullW}px`,
        height: `${fullH}px`,
        direction: "rtl",
        color: "#ffffff",
        background: "#081423",
      },
    });

    host.remove();

    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = EXPORT_FILENAME;
    link.click();

    setOk("تم تنزيل الصورة.");
  } catch (e) {
    setErr("فشل تصدير الصورة. جرّب مرة ثانية.");
  } finally {
    setExporting(false);
  }
};


  const statusClass =
    status.tone === "ok"
      ? "border-[#5fcc9c55] bg-[#5fcc9c14] text-[#5fcc9c]"
      : status.tone === "err"
      ? "border-[#ff4d4d55] bg-[#ff4d4d14] text-[#ff4d4d]"
      : "border-white/15 bg-white/5 text-white/80";

  return (
    <div className="min-h-screen text-white" dir="rtl">
      <ConfirmModal
        open={confirm.open}
        title={confirm.title}
        description={confirm.description}
        danger
        busy={savingSchedule}
        confirmText={confirm.kind === "clear" ? "مسح" : "حذف"}
        cancelText="إلغاء"
        onConfirm={onConfirmAction}
        onClose={() => (!savingSchedule ? setConfirm((c) => ({ ...c, open: false })) : null)}
      />

      <div className="max-w-6xl mx-auto px-4 md:px-8 pt-10 pb-16">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <img
                src={SITE_LOGO}
                alt="logo"
                className="w-12 h-12 rounded-xl border border-[#28e3da55] bg-white/5 object-contain"
                draggable={false}
              />
              <div>
                <h1 className="text-lg md:text-2xl font-extrabold">تحويل جدول المحاضرات لرمضان</h1>
                <p className="text-white/70 text-sm md:text-base">حول أوقات محاضراتك الرسمية إلى توقيت شهر رمضان المبارك.</p>
              </div>
            </div>

            <div className="flex items-center gap-3 justify-start md:justify-end flex-wrap">
              <button
                onClick={saveScheduleNow}
                disabled={disableActions}
                className="px-4 py-3 rounded-xl font-extrabold border transition border-white/15 bg-white/5 hover:bg-white/10 disabled:opacity-50"
              >
                {savingSchedule ? "جاري الحفظ..." : "حفظ الجدول"}
              </button>

              <button
                onClick={saveTableAsImage}
                disabled={disableActions}
                className="px-4 py-3 rounded-xl font-extrabold border transition border-[#28e3da55] bg-[#28e3da33] hover:bg-[#28e3da44] disabled:opacity-50"
              >
                {exporting ? "جاري..." : "حفظ كصورة"}
              </button>

              <button
                onClick={askClearAll}
                disabled={disableActions || rows.length === 0}
                className="px-4 py-3 rounded-xl font-extrabold border border-red-500/35 bg-red-500/15 hover:bg-red-500/25 transition disabled:opacity-50"
              >
                مسح
              </button>
            </div>
          </div>

          {status.msg ? (
            <div className={`mt-4 text-sm rounded-xl border px-4 py-3 ${statusClass}`}>{status.msg}</div>
          ) : null}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_.8fr] gap-4 mt-4">
          <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden p-4">
            <h2 className="font-extrabold mb-4 text-[#28e3da]">إضافة مادة جديدة</h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div className="md:col-span-2">
                <label className="text-xs text-white/70">اسم المادة</label>
                <input
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                  className="mt-1 w-full rounded-xl bg-[#0f1e2e] border border-[#28e3da40] px-3 py-3 outline-none focus:border-[#28e3da]"
                  placeholder="مثال: هندسة البرمجيات"
                  disabled={disableActions}
                />
              </div>

              <div>
                <label className="text-xs text-white/70">الوقت الحالي</label>
                <input
                  type="time"
                  value={beforeTime}
                  onChange={(e) => setBeforeTime(e.target.value)}
                  className="mt-1 w-full rounded-xl bg-[#0f1e2e] border border-[#28e3da40] px-3 py-3 outline-none focus:border-[#28e3da]"
                  disabled={disableActions}
                />
              </div>

              <div>
                <label className="text-xs text-white/70">المدة</label>
                <select
                  value={durBefore}
                  onChange={(e) => setDurBefore(e.target.value)}
                  className="mt-1 w-full rounded-xl bg-[#0f1e2e] border border-[#28e3da40] px-3 py-3 outline-none focus:border-[#28e3da]"
                  disabled={disableActions}
                >
                  <option value="50">50 دقيقة</option>
                  <option value="75">75–80 دقيقة</option>
                  <option value="100">100 دقيقة</option>
                </select>
              </div>

              <div className="md:col-span-4">
                <label className="text-xs text-white/70">الأيام</label>
                <DayMultiSelect value={selectedDays} onChange={setSelectedDays} disabled={disableActions} />
              </div>

              <button
                onClick={addRow}
                disabled={disableActions}
                className="md:col-span-4 w-full rounded-xl font-black text-white h-[44px] bg-[#28e3da40] hover:bg-[#28e3da60] transition disabled:opacity-50"
              >
                إضافة للجدول
              </button>
            </div>

            <div className="mt-4 text-xs text-white/55 leading-6">
              * يتم الحفظ تلقائيًا بعد الإضافة/الحذف/المسح.
              <br />
              * اختصار الأيام في الجدول: U الأحد، M الإثنين، T الثلاثاء، W الأربعاء، R الخميس.
              <br />
              * إذا كان وقت رمضان الناتج بعد 9:00 غالبًا تعتبر”عن بُعد”.
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/5 p-4 flex items-center justify-between">
            <div>
              <div className="text-white/60 text-xs">إحصائيات الجدول</div>
              <div className="text-3xl font-black">
                {rows.length} <span className="text-sm font-normal text-white/40">مواد</span>
              </div>
              <div className="text-xl font-bold text-[#5fcc9c]">{computed.okCount} محولة</div>
            </div>
            <ProgressCircle pct={computed.pct} />
          </div>
        </div>

        <div
          ref={exportRootRef}
          className="mt-4 relative rounded-2xl overflow-hidden border border-white/10 text-white"
          style={{ background: "#081423" }}
        >
          <div className="absolute inset-0 opacity-40 pointer-events-none">
            <img
              src="/images/stars.png"
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => (e.currentTarget.style.display = "none")}
              draggable={false}
            />
          </div>

          <div className="relative p-4 md:p-6 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={SITE_LOGO} className="w-10 h-10 object-contain" alt="logo" draggable={false} />
              <div>
                <div className="font-black text-lg">{SITE_NAME}</div>
                <div className="text-white/50 text-xs">جدول المحاضرات في رمضان</div>
              </div>
            </div>
            <div className="text-xs font-bold bg-white/10 px-3 py-1 rounded-full text-[#28e3da]">توقيت مكة المكرمة</div>
          </div>

          <div className="relative px-4 pt-4 md:px-6 md:pt-6">
            <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-[11px] text-white/70 leading-6">
              اختصار الأيام: U = الأحد، M = الإثنين، T = الثلاثاء، W = الأربعاء، R = الخميس
            </div>
          </div>

          <div ref={exportScrollRef} data-export-scroll="1" className="relative p-4 md:p-6 overflow-x-auto">

            <table className="text-right w-full border-separate [border-spacing:0_8px] text-right">
              <thead>
                <tr className="text-right text-[10px] text-white/40">
                  <th className="px-4 text-right">المادة</th>
                  <th className="px-4 text-right">الأيام</th>
                  <th className="px-4 text-right">الوقت المعتاد</th>
                  <th className="px-4 text-[#28e3da] text-right">وقت رمضان</th>
                  <th className="px-4 text-right">المدة الجديدة</th>
                  <th className="px-4 text-right">الحالة</th>
                  <th className="px-4 text-right"></th>
                </tr>
              </thead>

              <tbody>
                {computed.list.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-20 text-white/30">لا توجد محاضرات مضافة حالياً.</td>
                  </tr>
                ) : (
                  computed.list.map((r) => {
                    const badge =
                      r.ok
                        ? r.remote
                          ? { text: "عن بُعد", cls: "bg-orange-500/20 text-orange-400" }
                          : { text: "حضوري", cls: "bg-green-500/20 text-green-400" }
                        : { text: "غير مدعوم", cls: "bg-red-500/20 text-red-400" };

                    const daysText = daysAbbrevFromKeys(r.days);

                    return (
                      <tr key={r.id} className="bg-white/5 group text-right">
                        <td className="px-4 py-4 font-bold border-r-2 border-[#28e3da00] group-hover:border-[#28e3da] transition-all">
                          {r.name}
                          {!r.ok && r.near && (
                            <div className="text-[10px] text-red-400 mt-1">
                              غير متوفر. الأقرب: {formatTime12(r.near.prev)} أو {formatTime12(r.near.next)}
                            </div>
                          )}
                        </td>

                        <td className="px-4 py-4 ">
                          <span className="inline-flex items-center gap-2">
                            <span className="px-2 py-1 rounded text-[11px] font-black border border-white/15 bg-white/5 text-white">
                              {daysText}
                            </span>
                            <span className="text-[10px] text-white/45 hidden md:inline">
                              {daysLabelFromKeys(r.days)}
                            </span>
                          </span>
                        </td>

                        <td className="px-4 py-4 text-white/60">{formatTime12(r.before)}</td>

                        <td className="px-4 py-4 font-black text-[#28e3da]">
                          {r.ok ? formatTime12(r.ramadan) : "—"}
                        </td>

                        <td className="px-4 py-4 text-white/60">
                          {r.durBefore} ⮕ {r.ok ? r.durAfter : "—"} د
                        </td>

                        <td className="px-4 py-4">
                          <span className={`px-2 py-1 rounded text-[10px] font-bold ${badge.cls}`}>{badge.text}</span>
                        </td>

                        <td className="px-4 py-4 text-left">
                          <button
                            onClick={() => askDeleteRow(r.id)}
                            disabled={disableActions}
                            className="text-white/25 hover:text-red-400 transition disabled:opacity-50"
                          >
                            حذف
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="relative px-4 pb-4 md:px-6 md:pb-6 text-xs text-white/45">hemhuj.com</div>
        </div>
      </div>
    </div>
  );
}
